import dynamic from 'next/dynamic';
import { rem } from 'polished';
import querystring from 'query-string';
import React, { FC, useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { FatalAttraction } from '@vpr/tracking';

import {
  actions,
  constants,
  Container as ActivityContainer,
  ActivityEmptyState,
  ScrollSubscriber,
  ActivityType as Activity,
  ActivityState,
} from '@vimeo/activity';
import { BigPictureClient, Event } from '@vimeo/bigpicture-client';
import { Box, Button, IconButton } from '@vimeo/bokeh';
import { Bell, CloseXFilled } from '@vimeo/bokeh-icons';
import { useIsBokeh } from '@vimeo/core/hooks/use-is-bokeh';
import { translate } from '@vimeo/i18n-runtime';
import { User } from '@vimeo/viewer';

import { Content, MOBILE_MAX_WIDTH, ScrollingLoader, Wrapper } from './style';

const { fetchOlderActivities, markAllActivitiesDisplayed, showAllActivitiesDisplayed } = actions;
const { ACTIVITYPAGE_PAGE_SIZE, TOPNAV_PAGE_SIZE, FA_COMPONENTS, FA_CONTAINERS, FA_KEYWORDS } =
  constants;
const Tip = dynamic(async () => {
  const { Tooltip } = await import('@vimeo/bokeh');
  return { default: Tooltip };
});

type Props = {
  fetchOlderActivities: (x: number) => any;
  markAllActivitiesDisplayed: () => void;
  showAllActivitiesDisplayed: () => void;
  shouldIndicateUndisplayedActivities: boolean;
  isRequestingOlderActivities: boolean;
  noOlderActivitiesExist: boolean;
  activities: Activity[];
  user: User;
  showRefreshedLihpChanges?: boolean;
  onClick?: VoidFunction;
};

const ActivityDropdown: FC<Props> = (props: Props) => {
  const {
    activities,
    isRequestingOlderActivities,
    noOlderActivitiesExist,
    fetchOlderActivities,
    user,
    onClick,
  } = props;

  const [expanded, setExpanded] = useState<boolean>(false);
  const [unavailable, setUnavailable] = useState<boolean>(true);

  const activityTopNav = useRef<HTMLDivElement>(null);
  const activityDropdown = useRef<HTMLDivElement>(null);

  const isMobile = () => window.innerWidth <= MOBILE_MAX_WIDTH;

  const lockBodyScroll = () => {
    if (isMobile() && document.documentElement.style.overflow !== 'hidden') {
      document.documentElement.style.overflowY = 'hidden';
    }
  };

  const unlockBodyScroll = () => {
    if (isMobile() && document.documentElement.style.overflow !== 'hidden') {
      document.documentElement.style.overflowY = '';
    }
  };

  const toggleDropdown = () => {
    if (expanded) {
      hideDropdown();
    } else {
      setExpanded(!expanded);
      lockBodyScroll();

      const hasNew = shouldShowActivityCounter();
      FatalAttraction.trackClick({
        container: FA_CONTAINERS.TOPNAV,
        component: FA_COMPONENTS.TOPNAV,
        keyword: FA_KEYWORDS[hasNew ? 'newNotifications' : 'noNotifications'],
      });

      BigPictureClient.sendEvent(
        new Event('open_activity_bell', 1, {
          product: 'Workflow',
          path: window.location.pathname,
          location: 'activity bell',
          user_id: user.id,
        }),
      );
    }
  };

  /**
   * Clicking on a DOM node outside of this component
   * should close the dropdown.
   */
  const onDocumentClick = (e: React.MouseEvent<HTMLElement>) => {
    if (e.target && !activityTopNav?.current?.contains(e.target as Node)) {
      hideDropdown();
    }
  };

  /**
   * Hitting the ESC key should close the dropdown.
   */
  const onKeyUp = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.keyCode === 27) {
      hideDropdown();
    }
  };

  const hideDropdown = () => {
    unlockBodyScroll();
    setExpanded(false);
  };

  const shouldShowActivityCounter = () =>
    props.shouldIndicateUndisplayedActivities &&
    location.pathname !== '/activity' &&
    querystring.parse(location.search).activityReferer !== 1;

  useEffect(() => {
    props
      .fetchOlderActivities(isMobile() ? ACTIVITYPAGE_PAGE_SIZE : TOPNAV_PAGE_SIZE)
      .then(() => {
        setUnavailable(false);
      })
      .catch(() => {
        setUnavailable(true);
      });
  }, []);

  useEffect(() => {
    if (expanded) {
      document.addEventListener<any>('click', onDocumentClick);
      document.addEventListener<any>('keyup', onKeyUp);

      props.markAllActivitiesDisplayed();
    } else {
      document.removeEventListener<any>('click', onDocumentClick);
      document.removeEventListener<any>('keyup', onKeyUp);

      props.showAllActivitiesDisplayed();
    }

    activityTopNav.current?.addEventListener('selectstart', () => false);
  }, [expanded]);

  return (
    <Wrapper ref={activityTopNav}>
      <Tip label={translate('Activity')} placement="bottom" isDisabled={isMobile()}>
        <IconButton
          aria-label="Bell Notification"
          variant="tertiary"
          icon={<Bell />}
          onClick={(e) => {
            if (!unavailable && !activityDropdown?.current?.contains(e.target as Node)) {
              toggleDropdown();
            }
            onClick?.();
          }}
          isActive={useIsBokeh() && expanded}
          size={{ base: 'sm', sm: 'md' }}
          _after={{
            content: `' '`,
            background: 'red',
            borderRadius: '50%',
            width: rem(8),
            height: rem(8),
            position: 'absolute',
            top: rem(8),
            right: rem(8),
            display: shouldShowActivityCounter() ? 'block' : 'none',
          }}
        />
      </Tip>
      <Box
        id="DropdownMenu"
        ref={activityDropdown}
        width={rem(360)}
        background="fill-blur"
        backdropFilter="blur(var(--vimeo-blur-lg))"
        borderRadius="md"
        boxShadow="var(--vimeo-shadows-md)"
        position="absolute"
        top={rem(47)}
        right={0}
        display={expanded ? 'block' : 'none'}
        zIndex={1}
        sx={{
          '@media screen and (max-width: 489px)': {
            position: 'fixed',
            width: '100vw',
            height: '100vh',
            top: 0,
            zIndex: 2,
          },
        }}
      >
        <Box
          id="Header"
          fontSize={rem(16)}
          lineHeight={rem(20)}
          padding="md"
          fontWeight="bold"
          color="text-primary"
          sx={{
            '@media screen and (max-width: 489px)': {
              display: 'flex',
              height: rem(41),
            },
          }}
        >
          <IconButton
            aria-label="Dismiss Activity"
            variant="tertiary"
            icon={<CloseXFilled boxSize="sm" />}
            size="sm"
            onClick={hideDropdown}
            display="none"
            sx={{
              '@media (max-width: 489px)': {
                display: 'block',
                top: rem(-5),
                marginRight: rem(10),
              },
            }}
          />
          {translate('Activity')}
        </Box>
        <ScrollSubscriber
          onScrolledToBottom={() => {
            if (!isRequestingOlderActivities && !noOlderActivitiesExist) {
              fetchOlderActivities(TOPNAV_PAGE_SIZE);
            }
          }}
        >
          {({ onScroll }: { onScroll: React.UIEventHandler }) => (
            <Content
              onScroll={onScroll}
              centered={
                !activities.length && (isRequestingOlderActivities || noOlderActivitiesExist)
              }
            >
              {activities.length ? (
                <div>
                  {activities.map((activity) => (
                    <ActivityContainer
                      key={activity.id}
                      showCta={false}
                      activity={activity}
                      fatalAttraction={{
                        container: FA_CONTAINERS.DROPDOWN,
                        component: FA_COMPONENTS.DROPDOWN,
                        keyword: FA_KEYWORDS[activity.type],
                      }}
                    />
                  ))}
                </div>
              ) : !isRequestingOlderActivities ? (
                <ActivityEmptyState />
              ) : null}

              {isRequestingOlderActivities ? <ScrollingLoader /> : null}
            </Content>
          )}
        </ScrollSubscriber>

        {activities?.length > 0 ? (
          <Box id="Footer">
            <Box display="flex" justifyContent="center" padding="sm">
              <Button
                as="a"
                href="/activity"
                onClick={() => {
                  FatalAttraction.trackClick({
                    container: FA_CONTAINERS.DROPDOWN,
                    component: FA_COMPONENTS.DROPDOWN,
                    keyword: FA_KEYWORDS.seeAll,
                  });
                }}
                size="sm"
                variant="tertiary"
              >
                {translate('See all')}
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Wrapper>
  );
};

const mapStateToProps = (state: ActivityState) => ({
  activities: state.activities,
  shouldIndicateUndisplayedActivities: state.shouldIndicateUndisplayedActivities,
  isRequestingOlderActivities: state.isRequestingOlderActivities,
  noOlderActivitiesExist: state.noOlderActivitiesExist,
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchOlderActivities: (pageSize: number) => dispatch(fetchOlderActivities(pageSize)),
    markAllActivitiesDisplayed: () => dispatch(markAllActivitiesDisplayed()),
    showAllActivitiesDisplayed: () => dispatch(showAllActivitiesDisplayed()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityDropdown);
